/// <reference types="vite/client" />

import type { FilesStorageSchema } from '@iconik-io/iconik-api/src/services/files';
import type { StorageMethod } from '@iconik-io/shared-lib/src/utils/constants/storages';

import type { VariantType } from './variants';

import AWSLogo from '../images/logos/aws-color.webp';
import AzureLogo from '../images/logos/azure.webp';
import B2Logo from '../images/logos/backblaze_only_logo.webp';
import GCSLogo from '../images/logos/google-cloud-storage.webp';
import ISGLogo from '../images/logos/iconik-storage-gateway.svg';
import S3CompatibleLogo from '../images/logos/s3-compatible.webp';

type Status = NonNullable<FilesStorageSchema['status']>;
type StorageStatus = { label: string; value: Status; style: VariantType };
export const storageStatus: StorageStatus[] = [
  { label: 'Active', value: 'ACTIVE', style: 'success' },
  { label: 'Inactive', value: 'INACTIVE', style: 'warning' },
  { label: 'Failing', value: 'FAILING', style: 'danger' },
];
export const storageStatusMap: Record<string, StorageStatus> = {};
storageStatus.forEach((item) => {
  storageStatusMap[item.value] = item;
});

export const SUPPORTED_VERIFY_METHODS: StorageMethod[] = [
  'S3',
  'GCS',
  'B2',
  'AZURE',
];

export const storageMethodLabels: Record<StorageMethod, string> = {
  FILE: 'FILE',
  HTTP: 'HTTP',
  FTP: 'FTP',
  SFTP: 'SFTP',
  S3: 'S3 Compatible',
  AZURE: 'Azure Blob Storage',
  GCS: 'Google Cloud Storage',
  B2: 'Backblaze B2',
  PORTAL: 'Portal',
  CUSTOM: 'Custom',
  DROPBOX: 'Dropbox',
};

export type StorageMethodOption = {
  label: string;
  value: StorageMethod;
  description: string;
};

export enum S3Type {
  AWS = 'aws',
  S3_COMPATIBLE = 's3_compatible',
}

export const STORAGE_FILES_PURPOSE = 'FILES';
export const STORAGE_KEYFRAMES_PURPOSE = 'KEYFRAMES';
export const STORAGE_PROXIES_PURPOSE = 'PROXIES';

export type StoragePurpose = FilesStorageSchema['purpose'];
export type StoragePurposeOption = {
  label: string;
  value: StoragePurpose;
  description: string;
};
export const storagePurposes: StoragePurposeOption[] = [
  {
    label: 'Files',
    value: 'FILES',
    description: 'stores all file types except keyframes and proxies',
  },
  {
    label: 'Keyframes',
    value: 'KEYFRAMES',
    description: 'stores only keyframes generated by iconik',
  },
  {
    label: 'Proxies',
    value: 'PROXIES',
    description: 'stores only proxies generated by iconik',
  },
  {
    label: 'Exports',
    value: 'EXPORTS',
    description: 'will be used as a destination for export jobs',
  },
  {
    label: 'Archive',
    value: 'ARCHIVE',
    description: 'will be used as an archive destination',
  },
  {
    label: 'Faces',
    value: 'FACES',
    description: 'stores extracted images for face recognition feature',
  },
];

export const storagePurposeMap: Record<string, StoragePurposeOption> = {};
storagePurposes.forEach((item) => {
  storagePurposeMap[item.value] = item;
});

// Maps the storage purposes that are included in the storage filter dropdown
// to their corresponding Elasticsearch nested term names.
export const STORAGE_PURPOSE_TERM_NAMES: Record<string, string> = {
  FILES: 'files.storage_id',
  ARCHIVE: 'files.storage_id',
  PROXIES: 'proxies.storage_id',
  KEYFRAMES: 'keyframes.storage_id',
};

export const STORAGE_ID_TERM_NAMES = new Set(
  Object.values(STORAGE_PURPOSE_TERM_NAMES),
);

export const STORAGE_METHOD_LOGO_URLS: Record<string, string> = {
  S3: AWSLogo,
  S3_COMPATIBLE: S3CompatibleLogo,
  GCS: GCSLogo,
  AZURE: AzureLogo,
  B2: B2Logo,
  FILE: ISGLogo,
};

export function getStorageLogoUrl(
  method: string | undefined | null,
  endpoint: string | undefined | null,
): string | undefined {
  if (method === 'S3') {
    return endpoint?.includes('amazonaws.com')
      ? STORAGE_METHOD_LOGO_URLS.S3
      : STORAGE_METHOD_LOGO_URLS.S3_COMPATIBLE;
  }
  return method ? STORAGE_METHOD_LOGO_URLS[method] : undefined;
}

// Storage wizard steps
export const STEP_DOWNLOAD = -1;
export const STEP_SETUP = 0;
export const STEP_CREDENTIALS = 1;
export const STEP_CONNECT = 2;
